/* eslint-disable no-console */
import { useEffect, useMemo, useState } from 'react';
import {
  getOrFetchSipStatus,
  peekSipStatusCache
} from '../infrastructure/voiceSipStatusCache';
import { lineSipEnabledByPhone } from '../domain/voiceSipStatus';

/**
 * One-shot outbound SIP gate for a business E.164.
 * Fetches aggregate SIP status at most once (module cache); switching `fromE164`
 * only re-looks up `lines[]` in memory.
 *
 * @param {{ enabled?: boolean, fromE164?: string }} [options]
 * @returns {{ checking: boolean, lineReady: boolean, checked: boolean }}
 */
export default function useVoiceOutboundLineSipGate(options = {}) {
  const enabled = options.enabled !== false;
  const fromE164 = options.fromE164 ? String(options.fromE164) : '';

  const [payload, setPayload] = useState(() => peekSipStatusCache());
  const [checking, setChecking] = useState(() => (
    Boolean(enabled && fromE164 && peekSipStatusCache() == null)
  ));
  const [fetchFailed, setFetchFailed] = useState(false);

  useEffect(() => {
    if (!enabled || !fromE164) {
      setChecking(false);
      return undefined;
    }

    const cached = peekSipStatusCache();
    if (cached != null) {
      setPayload(cached);
      setChecking(false);
      setFetchFailed(false);
      return undefined;
    }

    let cancelled = false;
    setChecking(true);

    getOrFetchSipStatus()
      .then((result) => {
        if (cancelled) return;
        setPayload(result);
        setFetchFailed(false);
      })
      .catch((error) => {
        console.error('[voice-outbound-sip-gate] SIP status check failed:', error);
        if (!cancelled) {
          setPayload(null);
          setFetchFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, fromE164]);

  const lineReady = useMemo(() => {
    if (!enabled || !fromE164 || fetchFailed || payload == null) return false;
    return lineSipEnabledByPhone(payload, fromE164);
  }, [enabled, fromE164, fetchFailed, payload]);

  const checked = Boolean(enabled && fromE164 && !checking && (payload != null || fetchFailed));

  return { checking, lineReady, checked };
}
